/* eslint-disable react/destructuring-assignment */
// High-order components must use the spreading operator
// to pass the props down to the real one.
/* eslint-disable react/jsx-props-no-spreading */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { connect } from 'react-redux';
import dayjs from 'dayjs';
import { getToken } from '../api/token';
import { isJsonString } from '../../utils/jsonParsing';
import { ToastError } from '../../ui/components/Toast';
import { LOADER_CLOSE, LOADER_OPEN } from '../redux/actions';

export function withFileUploader(LazyComp) {
  //eslint-disable-next-line
  const WithFileUploader = (props) => {
    const [progress, setProgress] = useState({});

    function createFormData(file, key) {
      if (!file) return;

      const formData = new FormData();

      let formFile = null;
      if (typeof File === 'function') {
        formFile = new File([file.file || file], file.name || file);
      } else {
        // IE11
        formFile = new Blob([file.file || file]);
      }

      formData.append(key, formFile);

      // eslint-disable-next-line consistent-return
      return formData;
    }

    const onFileLoad = ({
      file,
      key = 'data',
      url,
      fileLoad = () => {},
      fileUpdate = () => {},
      closeLoader = () => {},
      errorCallback,
      body = {},
    }) => {
      if (!(file && file.id)) return;

      const { token } = getToken();

      const formData = createFormData(file, key);
      if (!formData) return;

      // add other fields in formData (if present)
      Object.keys(body).forEach((k) => {
        formData.append(k, body[k]);
      });
      const fileUpload = new XMLHttpRequest();

      fileUpload.upload.addEventListener(
        'progress',
        (progressHandler) => {
          const { loaded = 0, total: target = 0 } = progressHandler;
          setProgress((s) => ({
            ...s,
            [file.id]: Math.ceil((loaded / target) * 100),
          }));
        },
        false
      );

      fileUpload.addEventListener(
        'load',
        (progressLoad) => {
          if (fileUpdate) {
            closeLoader();
            const { target: { response: data = {} } = {} } = progressLoad;

            if (data?.code !== 200) {
              if (typeof errorCallback === 'function') {
                errorCallback(data?.data?.message);
              } else {
                toast.error(
                  <ToastError
                    title="Qualcosa è andato storto"
                    body="Riprova più tardi"
                  />
                );
              }
              return;
            }
            const updatedFile = {
              ...file,
              url: data?.data?.url || '',
            };
            fileUpdate(updatedFile, data?.data);
          }
        },
        false
      );

      fileUpload.addEventListener(
        'error',
        (error) => {
          // eslint-disable-next-line no-console
          console.log('fileUploadCall error', error);
          closeLoader();
        },
        false
      );

      fileUpload.addEventListener(
        'abort',
        (abortHandler) => {
          // eslint-disable-next-line no-console
          console.log('abort', abortHandler);
          closeLoader();
        },
        false
      );

      fileUpload.open('POST', url);
      fileUpload.setRequestHeader('Authorization', `Bearer ${token}`);
      fileUpload.responseType = 'json';
      fileUpload.send(formData);
      fileLoad(file);
    };

    const onFileChange = ({
      file,
      key = 'data',
      url = '',
      callback = () => {},
      errorCallback,
      format,
      body = {},
    }) => {
      props?.loaderOpen();
      const fileToUpload = file;
      const reader = new FileReader();

      // eslint-disable-next-line consistent-return
      const getBlob = (evt) => {
        if (evt.target.readyState === FileReader.DONE) {
          // DONE == 2
          return new Blob([evt.target.result]);
        }
      };

      const getFile = (evt) => {
        switch (format) {
          case 'BASE64':
            return reader.result;

          case 'BINARY':
          default:
            return getBlob(evt);
        }
      };

      reader.onabort = (evt) => {
        // eslint-disable-next-line no-console
        console.log('FileUpload > reader.onabort', evt);
      };
      reader.onerror = (err) => {
        // eslint-disable-next-line no-console
        console.log('FileUpload > reader.onerror', err);
      };
      reader.onload = (e) => {
        const { name, size, type } = fileToUpload;
        onFileLoad({
          file: {
            name,
            size,
            type,
            id: dayjs().format('DDHHmmss'),
            file: getFile(e),
          },
          key,
          body,
          url,
          fileLoad: () => {},
          fileUpdate: callback,
          closeLoader: () => props?.loaderClose(),
          errorCallback,
        });
      };

      if (!fileToUpload) props?.loaderClose();
      switch (format) {
        case 'BASE64':
          reader.readAsDataURL(fileToUpload);
          break;

        case 'BINARY':
        default:
          reader.readAsArrayBuffer(fileToUpload);
          break;
      }
    };

    return (
      <LazyComp
        {...props}
        onFileLoad={onFileLoad}
        onFileChange={onFileChange}
        uploadProgress={progress}
      />
    );
  };

  WithFileUploader.propTypes = {
    onFileUpload: PropTypes.func,
    loaderOpen: PropTypes.func,
    loaderClose: PropTypes.func,
  };

  WithFileUploader.displayName = `WithFileUploader(${
    LazyComp?.displayName || LazyComp?.name || 'Component'
  })`;

  return connect(
    (state) => ({}),
    (dispatch) => ({
      loaderOpen: () => dispatch({ type: LOADER_OPEN }),
      loaderClose: () => dispatch({ type: LOADER_CLOSE }),
    })
  )(WithFileUploader);
}
